import React from 'react'
import { connect } from 'react-redux'
import { Content, Flex, Button, Toast, Text, List } from '@leverege/ui-elements'
import { Project } from '@leverege/api-redux'
import ModelUtil from '@leverege/model-util'

import { BreadcrumbActions } from '../../actions'

import OidcModel from './OidcModel'
import OidcEditor from './OidcEditor'
import OidcList from './OidcList'
import AddDialog from './AddDialog'
import EditModel from './EditModel'

import S from './OidcScreen.css'

const { changeObject } = ModelUtil

/**
 * @class OidcScreen - A screen for creating, viewing, updating, and deleting OpenID Connect clients within a given Imagine project.
 * @component 
 */
class OidcScreen extends React.Component {

  constructor( props ) {
    super( props )
    this.state = { 
      showAdd : false, 
      actionState : null,
      selection : null, 
    }
    // This selector manages the selection logic for items in the oidc clients list.
    this.selector = new List.SingleSelector( this, 'selection', { idKey : 'id' } )
  }

  async componentDidMount() {
    const { dispatch, projectId } = this.props
    const project = new Project( projectId )
    dispatch( project.oidcClients().list() )
    dispatch( BreadcrumbActions.setBreadcrumbs( [] ) )
  }

  /**
   * @method onAdd Show the `AddDialog` component 
   * @param {object} evt An event object 
   */
  onAdd = ( evt ) => {
    this.setState( { showAdd : true } )
  }

  /**
   * @method onClose Hide the `AddDialog` component 
   * @param {object} evt An event object 
   */
  onClose = ( evt ) => {
    this.setState( { showAdd : false } )
  }

  /**
   * @method onSelect Navigate to a detail route for the oidc client a user selected
   * @param {object} evt An event object 
   */
  onSelect = ( evt ) => {
    const { history, projectId } = this.props
    history.push( `/project/${projectId}/oidc/client/${evt.id}` )
  }

  /**
   * @method getSelectedClient 
   * @param {object}
   */
  getSelectedClient = () => { 
    const { selection } = this.state 
    const { oidcClients } = this.props 

    return typeof selection === 'string' ? oidcClients[selection] : null
  }

  /**
   * @method onAddClient Add a new OIDC Client and show success or failure toasts afterward 
   * @param {object} clientData The populated data model for a new OIDC client object
   */
  onAddClient = ( clientData ) => {
    const { dispatch, history, projectId } = this.props

    this.onClose()

    dispatch( Project( projectId )
      .oidcClients()
      .create( clientData ) )
      .then( ( data ) => {
        history.push( `/project/${projectId}/oidc/client/${data.id}` )
        Toast.success( `Client ${data.name} Created Successfully`, {} )
      },
      ( err ) => {

        const msg = (
          <div>
            <Text variant="toastTitle">
              Client Creation Failed. See @leverege/api README For Client Requirements.
            </Text>
            <Text variant="toastMsg">{err.message}</Text>
          </div>
        )

        Toast.error( msg, { } )
      }
      )
  }

  /**
   * @method onDeleteClient Delete an existing OIDC Client and show success or failure toasts afterward 
   * @param {object} client The oidc client being deleted
   */
  onDeleteClient = ( client ) => {
    const { dispatch, history, projectId } = this.props

    this.setState( { actionState : 'deleting' } ) 

    dispatch( Project( projectId ).oidcClient( client.id ).delete() )
      .then( () => {
        this.setState( { selection : null, actionState : 'deleted' } ) 
        const path = history.location.pathname
        history.replace( path.substring( 0, path.lastIndexOf( '/' ) ) )
        Toast.success( `Client ${client.name} Deleted Successfully`, {} )
      },
      ( err ) => {
        this.setState( { actionState : 'deleteError' } ) 

        const msg = (
          <div>
            <Text variant="toastTitle">Client Deletion Failed</Text>
            <Text variant="toastMsg">{err.message}</Text>
          </div>
        )
        Toast.error( msg, { } )
      } )
  }

  /**
   * @method onSaveClient Save updates to an OIDC Client and show success or failure toasts afterward 
   * @param {object} event The event object sent to the onSaveClient method
   * @param {object} event.value The new oidc client values
   * @param {object} event.oldValue The old oidc client values 
   */
  onSaveClient = ( { value, oldValue } ) => {
    let updating = false
    const { dispatch, projectId } = this.props
    
    const opts = {
      undefinedIsNull : true,
      keys : [
        'name',
        'alias',
        'description',
        'discoveryUrl',
        'clientId',
        'clientSecret',
        'scope',
        'allowUserCreation'
      ]
    }
    const change = changeObject( value, oldValue, opts )
    
    if ( Object.keys( change ).length > 0 ) {
      updating = true
      dispatch( Project( projectId ).oidcClient( value.id ).update( change ) )
        .then( ( e ) => { 
          this.setState( { actionState : null } ) 
          Toast.success( `Client ${value.name} Updated Successfully`, {} )
        }, ( err ) => {
          this.setState( { actionState : 'saveError' } ) 
          Toast.error( err.message, {} )
        } )
    }

    if ( updating ) {
      this.setState( { actionState : 'saving' } ) 
    } else {
      Toast.success( 'No Changes to Save', {} )
    }

  }

  render() {
    const { showAdd, actionState } = this.state
    const { oidcClients, history } = this.props 

    const selectedClient = this.getSelectedClient()
    
    return (
      <Content>
        <Content.Header>
          <Flex className={S.header} variant="rowM" justify="space-between" >
            <Text variant="screenTitle">OpenID Connect</Text>
            <Button onClick={this.onAdd} eventData="role">Create Client</Button>
          </Flex>
        </Content.Header>
        <Content.Area>
          <Content vFill horizontal>
            <Content.Area>
              <OidcList clients={oidcClients} selector={this.selector} onSelect={this.onSelect} />
            </Content.Area>

            <Content.Footer className={S.editor}>
              <AddDialog
                title="Create OpenID Connect Client"
                editor={OidcEditor}
                creator={OidcModel}
                onClose={this.onClose}
                onCreate={this.onAddClient}
                show={showAdd} />
              <EditModel 
                editor={OidcEditor}
                history={history}
                title={selectedClient?.name || 'OpenID Connect Client'}
                actionState={actionState}
                saveOnChange={false}
                deleteTitle={`Delete ${selectedClient?.name || 'This OIDC Client'}?`}
                deleteMessage="This will permanently remove this OpenID Connect client from your project."
                value={selectedClient} 
                onDelete={this.onDeleteClient} 
                onSave={this.onSaveClient} />
            </Content.Footer>
          </Content>
        </Content.Area>
      </Content>
    )
  }
}

export default connect(
  ( state, props ) => {
    const projectId = props.match?.params?.projectId 
    return {
      systems : state.systems,
      oidc : state.oidc || {},
      oidcClients : state.oidc?.[projectId]?.oidcClients || {},
      projectId,
    }
  }
)( OidcScreen )
